import {useDebounce} from '/src/com/yext/listingslaunchstorm/js/common/hooks/usedebounce';
import {LinkedAccount} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/linkedaccount';
import {useValidateFacebookBrand} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/datasource/usevalidatefacebookbrand';
import {Button} from '/ui/components/button/button';
import {Checkbox} from '/ui/components/checkbox/checkbox';
import {Input} from '/ui/components/input/input';
import {Table} from '/ui/components/table/table';
import {Tooltip} from '/ui/components/tooltip/tooltip';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/facebook/facebook.module.scss';
import CheckedSvg from '/src/com/yext/listingslaunchstorm/public/icons/green-checkmark.svg';

/**
 * @param {{
 *  linkedAccount: LinkedAccount,
 *  brandId: (string | undefined),
 *  skipBrandId: boolean,
 *  setBrandConfiguration: function(boolean, string=): void,
 *  disabled: boolean,
 * }} props
 */
export function FacebookBrandInput({linkedAccount, setBrandConfiguration, brandId, skipBrandId, disabled}) {
  const [brandIdInput, setBrandIdInput] = React.useState(brandId ?? '');
  const [inProgress, setInProgress] = React.useState(false);
  const {status, data, refetch, abort} = useValidateFacebookBrand();

  const validate = React.useCallback((linkedAccountId, newBrandId) => {
    if (newBrandId.length > 0) {
      refetch(linkedAccountId, newBrandId);
      setInProgress(false);
    }
  }, [refetch]);

  const {debouncedCallback, dispose} = useDebounce(validate, 1000);

  React.useEffect(() => {
    setInProgress(true);
    dispose();
    abort();
  }, [linkedAccount, dispose, abort]);

  const isError = status === 'error';
  const isLoading = brandIdInput.length === 0 || inProgress || status === 'loading';
  const isValid = data !== undefined && data.valid;
  const isInvalid = data !== undefined && !data.valid;

  React.useEffect(() => {
    if (inProgress) {
      debouncedCallback(linkedAccount.id, brandIdInput);
    } else if (!skipBrandId && isValid) {
      setBrandConfiguration(false, brandIdInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, isValid, brandIdInput]);

  return (
    <div className={styles.facebookBrandInput}>
      <FacebookBrandPageSetupInstructions />
      <br/>
      <Table
        columns={getColumns()}
        dataRows={[
          {
            id: 'brandIdMenu',
            [COLUMN_IDS.BRAND_ID]:
              <ul>
                <li>
                  <FacebookBrandValidationMessage
                    brandIdInput={brandIdInput}
                    skipBrandId={skipBrandId}
                    linkedAccountId={linkedAccount.id}
                    isLoading={isLoading}
                    isError={isError}
                    isValid={isValid}
                    isInvalid={isInvalid}
                    validate={validate}
                  />
                </li>
                <li>
                  <div className={styles.facebookBrandRow}>
                    <div className={styles.facebookBrandText}>
                      <Input
                        label={yext.msg('Brand Page ID')}
                        hideLabel={true}
                        type="text"
                        valid={isLoading || (!isError && !isInvalid)}
                        value={brandIdInput}
                        onChange={newBrandId => {
                          setBrandIdInput(newBrandId);
                          setInProgress(true);
                        }}
                        disabled={skipBrandId || disabled}
                        placeholder={yext.msg('Enter Brand Page ID')}
                      />
                    </div>
                    <SkipFacebookBrandCheckbox skipBrandId={skipBrandId} setBrandConfiguration={setBrandConfiguration} isDisabled={disabled} setBrandIdInput={setBrandIdInput}/>
                  </div>
                </li>
              </ul>,
          },
        ]}
        label={yext.msg('Brand Page ID Input')}
      />
    </div>
  );
}

function FacebookBrandPageSetupInstructions() {
  const brandPageText = yext.msg(
    '• <strong>Brand Page</strong>: Enter your Brand Page ID if you manage individual pages <strong>linked</strong> to a main brand page',
  );
  const standalonePageText = yext.msg(
    '• <strong>Standalone</strong>: Select ‘Skip Inputting Brand Page ID’ if you manage individual pages that are <strong>not linked</strong> to a main brand page',
  );

  return (
    <div>
      <p>{yext.msg('Facebook Setup depends on your structure:')}</p>
      <div dangerouslySetInnerHTML={{__html: brandPageText}} />
      <div dangerouslySetInnerHTML={{__html: standalonePageText}} />
    </div>
  );
}

const SkipFacebookBrandCheckbox = React.memo(
  function SkipFacebookBrandCheckbox({skipBrandId, setBrandConfiguration, setBrandIdInput, isDisabled}) {
    return <div className={styles.facebookBrandCheckbox}>
      <Checkbox
        checked={skipBrandId}
        disabled={isDisabled}
        label={yext.msg('Skip Inputting Brand Page ID')}
        value={skipBrandId}
        onChange={() => {
          setBrandConfiguration(!skipBrandId, undefined);
          setBrandIdInput('');
        }}
      />
    </div>;
  });

const FacebookBrandValidationMessage = React.memo(
  function FacebookBrandValidationMessage({brandIdInput, linkedAccountId, skipBrandId, isLoading, isError, isValid, isInvalid, validate}) {
    if (skipBrandId || isLoading) {
      return <div></div>;
    }
    if (isError) {
      return <div className={styles.facebookBrandError}>
        <>
          {yext.msg('There was an error validating this brand page ID.')}&nbsp;
          <Button chromeless={true} onClick={() => validate(linkedAccountId, brandIdInput)}>{yext.msg('Please click to retry.')}</Button>
        </>
      </div>;
    }
    if (isValid) {
      return <div className={styles.facebookBrandValid}>
        <CheckedSvg />{yext.msg('Valid!')}
      </div>;
    }
    if (isInvalid) {
      return <div className={styles.facebookBrandError}>
        {yext.msg('Invalid. ID may be incorrect or unauthorized.')}
      </div>;
    }
    return <div></div>;
  });


const COLUMN_IDS = {
  BRAND_ID: 'brandId',
};

function getColumns() {
  return [
    {
      id: COLUMN_IDS.BRAND_ID,
      label: <Tooltip
        unclearText={yext.msg('Brand Page ID')}
        tooltipText={yext.msg('To locate your ID, go to your brand page, select the About tab, and then select Page Transparency.\n\nIf you skip the brand page ID, your Facebook locations will be matched to standalone pages that aren\'t linked to a brand page.')}
      />,
    },
  ];
}
